import { useT } from "../lib/i18n.jsx";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api.js";
import { IconCheck, IconArrow } from "../components/icons.jsx";

function fmtCountdown(ms) {
  if (ms <= 0) return "expired";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function DeviceCodePage() {
  const { t } = useT();
  const [generated, setGenerated] = useState(null); // { code, expires_at }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  async function generate() {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res = await api("/sso/paste/generate", {
        method: "POST",
        body: {},
      });
      setGenerated(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function copyCode() {
    if (!generated) return;
    navigator.clipboard.writeText(generated.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const remaining = generated
    ? new Date(generated.expires_at).getTime() - now
    : 0;
  const expired = generated && remaining <= 0;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Vorks Connect</div>
          <h1 className="h1">Pair a device by code</h1>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h2>One-time device code</h2>
        </div>
        <div className="section-body">
          <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
            Use this if the device you want to install Vorks Connect on can't
            open a browser. Generate a code here and type it into the client's
            sign-in screen.
          </p>

          {!generated && (
            <button
              className="btn primary lg"
              onClick={generate}
              disabled={busy}
              style={{ marginTop: 12 }}
            >
              {busy ? "Generating…" : "Generate device code"} <IconArrow size={14} />
            </button>
          )}

          {error && <div className="error-line">{error}</div>}

          {generated && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              style={{
                marginTop: 18,
                padding: 32,
                background: expired ? "var(--bg)" : "linear-gradient(135deg, var(--bg-elev) 0%, var(--bg-elev-2) 100%)",
                border: "1px solid " + (expired ? "var(--line)" : "var(--acc)"),
                borderRadius: "var(--r-lg)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {!expired && (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 32, right: 32,
                  height: 2,
                  background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
                  boxShadow: "0 0 10px var(--acc-glow)",
                }} />
              )}

              <div className="eyebrow" style={{ color: expired ? "var(--err)" : undefined }}>
                {expired ? "Expired" : "Your code"}
              </div>

              <div
                className="mono"
                style={{
                  fontSize: 44,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: expired ? "var(--text-faint)" : "var(--acc)",
                  textShadow: expired ? "none" : "0 0 20px var(--acc-glow)",
                  margin: "12px 0",
                  textAlign: "center",
                  userSelect: "all",
                  cursor: "pointer",
                }}
                onClick={copyCode}
                title="Click to copy"
              >
                {generated.code}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                <span className="mono" style={{
                  fontSize: 11,
                  color: expired ? "var(--err)" : "var(--text-dim)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}>
                  {expired ? "code is no longer valid" : `expires in ${fmtCountdown(remaining)}`}
                </span>
                <button
                  className="btn ghost"
                  onClick={copyCode}
                  disabled={expired}
                  style={{ padding: "6px 12px" }}
                >
                  {copied ? <><IconCheck size={11} /> Copied</> : "Copy"}
                </button>
              </div>

              {expired ? (
                <button
                  className="btn primary full"
                  onClick={generate}
                  disabled={busy}
                  style={{ marginTop: 16 }}
                >
                  Generate new code
                </button>
              ) : (
                <p className="mono" style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 16, lineHeight: 1.5 }}>
                  Open Vorks Connect on the device you want to install on. Click
                  "Sign in with code" and paste this code.
                </p>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
}
